// Хелперы переименования спикеров в плоском тексте транскрипта.
// Метка спикера — префикс начала строки до двоеточия: «Спикер 1: …»,
// «Менеджер: …», «Иван: …». Длину метки ограничиваем (≤40), чтобы не
// цеплять двоеточия внутри обычных предложений.
//
// Между меткой и двоеточием может стоять таймкод — «[УЧАСТНИК_1] [00:00:00 -
// 00:00:05]: …». Он содержит собственные двоеточия, и без отдельной ветки
// разбора строка не распознавалась вовсе: кнопка переименования молча ничего
// не меняла в тексте с таймкодами, хотя в его копии без таймкодов метки
// менялись (22.08.2026).

// (1) отступ (2) метка (3) необязательный таймкод (4) пробел после двоеточия
const LABEL_RE =
  /^(\s*)(\[[^[\]\n]{1,40}\]|[^:\n[\]]{1,80}?)[ \t]*(\[\d{1,2}:\d{2}:\d{2}[^\]\n]*\])?[ \t]*:(\s)/;

// Метка длиннее сорока знаков — только с таймкодом за ней: имя из Zoom
// («Елизавета Кайбазакова, Лаб ИИ, Питер») длинное, а длинная проза до
// двоеточия без таймкода меткой не бывает. Тот же порог, что в
// lib/transcriptParse.js — разойдутся, и участник виден в долях, но не в
// окне переименования (ревью 05.10).
const LABEL_CHARS_NO_TIMECODE = 40;

function matchLabel(line) {
  const m = line.match(LABEL_RE);
  if (!m) return null;
  return m[3] || m[2].trim().length <= LABEL_CHARS_NO_TIMECODE ? m : null;
}

// Строка без участника, с одним таймкодом («[00:00:00 - 00:00:05]: текст»),
// попадает в ту же ветку разбора. Метки там нет, переименовывать нечего.
const TIMECODE_LABEL_RE = /^\[?\d{1,2}:\d{2}:\d{2}/;

// Уникальные метки спикеров в порядке появления.
export function extractSpeakerLabels(text) {
  const labels = [];
  const seen = new Set();
  for (const line of (text || '').split('\n')) {
    const m = matchLabel(line);
    if (!m) continue;
    const label = m[2].trim();
    if (!label || TIMECODE_LABEL_RE.test(label)) continue;
    if (!seen.has(label)) { seen.add(label); labels.push(label); }
  }
  return labels;
}

// Похоже ли, что перед нами размеченная по говорящим расшифровка.
//
// 🔴 Наличие меток само по себе ничего не доказывает. LABEL_RE считает меткой
// любое слово до двоеточия в начале строки, и на обычной речи — «Итак:»,
// «Вопрос:», «Ссылка: https://…» — оно срабатывает. Кнопка «Имена участников»
// из-за этого показывалась на тексте, который никто не диаризовал
// (найдено владельцем 07.09.2026).
//
// Настоящую разметку отличают две вещи разом: метки ПОВТОРЯЮТСЯ (говорящий
// произносит не одну реплику) и покрывают БОЛЬШИНСТВО строк. Отдельно взятая
// «Ссылка:» посреди абзацев не проходит ни по одному признаку.
//
// Заглушки диаризации («Спикер 1», «SPEAKER_00») — достаточный признак сами по
// себе: такие метки из живой речи не берутся.
const DIARIZED_SHARE = 0.6;

export function looksDiarized(text) {
  const lines = (text || '').split('\n').filter((l) => l.trim());
  if (!lines.length) return false;

  const counts = new Map();
  let labelled = 0;
  for (const line of lines) {
    const m = matchLabel(line);
    if (!m) continue;
    const label = m[2].trim();
    if (!label || TIMECODE_LABEL_RE.test(label)) continue;
    labelled += 1;
    counts.set(label, (counts.get(label) || 0) + 1);
  }
  if (!counts.size) return false;
  if ([...counts.keys()].some(isGenericSpeakerLabel)) return true;

  const repeated = [...counts.values()].some((n) => n > 1);
  return repeated && labelled / lines.length >= DIARIZED_SHARE;
}

// Заменяет метку в начале строки по map (старое→новое). Пустые/неизменённые
// значения игнорируются; таймкод и реплика после двоеточия не трогаются.
export function applySpeakerRename(text, map) {
  if (!text) return text;
  return text.split('\n').map((line) => {
    const m = matchLabel(line);
    if (!m) return line;
    const label = m[2].trim();
    if (TIMECODE_LABEL_RE.test(label)) return line;
    const renamed = map[label] && map[label].trim();
    if (renamed && renamed !== label) {
      const timecode = m[3] ? ` ${m[3]}` : '';
      return `${m[1]}${renamed}${timecode}:${m[4]}${line.slice(m[0].length)}`;
    }
    return line;
  }).join('\n');
}

// Метка на экране приходит в скобках («[Владимир]», «[УЧАСТНИК_2]»), а имя
// голоса хранится без них. Без этой чистки повторное сохранение заводило бы
// второй отпечаток «[Владимир]» рядом с «Владимир».
export function cleanSpeakerName(label) {
  return String(label || '').trim().replace(/^\[/, '').replace(/\]$/, '').trim();
}

// «Участник 2», «СПИКЕР_3», «SPEAKER_00» — не имя человека, а заглушка.
// Такое в базу голосов пускать нельзя.
const GENERIC_RE = /^(участник|спикер|speaker)[\s_-]*\d+$/i;

export function isGenericSpeakerLabel(name) {
  return GENERIC_RE.test(cleanSpeakerName(name));
}
